import { useEffect, useMemo, useState } from "react";
import TicketCard, { type RemovalTicket } from "../components/TicketCard";
import ProofDrawer from "../components/ProofDrawer";
import type { ExposureStatus } from "../components/ExposureCard";
import { listTickets, type Ticket as ApiTicket } from "../lib/api";

const NAVY = "#0b1f4a";
const ACCENT_100 = "#fff3e4";
const ACCENT_300 = "#facb8d";
const ACCENT_900 = "#3a270d";

type ViewMode = "lista" | "board";

const BOARD_COLS: { title: string; keys: ExposureStatus[] }[] = [
  { title: "I kö", keys: ["QUEUED", "DISCOVERED"] },
  { title: "Pågår", keys: ["SUBMITTED", "ACKNOWLEDGED", "NEEDS_USER_ACTION"] },
  { title: "Klart", keys: ["REMOVED"] },
  { title: "Nekad / Återkommen", keys: ["DENIED", "REAPPEARED"] },
];

const KNOWN_STATUSES: ExposureStatus[] = [
  "DISCOVERED",
  "QUEUED",
  "SUBMITTED",
  "ACKNOWLEDGED",
  "REMOVED",
  "DENIED",
  "NEEDS_USER_ACTION",
  "REAPPEARED",
];

function asStatus(s: string): ExposureStatus {
  if ((KNOWN_STATUSES as string[]).includes(s)) return s as ExposureStatus;
  const alias: Record<string, ExposureStatus> = {
    sent: "SUBMITTED",
    draft: "QUEUED",
    found: "DISCOVERED",
    responded: "ACKNOWLEDGED",
    denied: "DENIED",
    done: "REMOVED",
  };
  return alias[s] ?? "QUEUED";
}

const day = (v: unknown) => {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return new Date(n).toISOString().slice(0, 10);
};

/** API-ärende → det kortet vill ha. */
function toCard(t: ApiTicket): RemovalTicket {
  const tl = (t.timeline ?? []) as { at: unknown; status: string; note?: string }[];
  return {
    id: t.id,
    source: t.sourceName || "Okänd källa",
    engine: null,
    status: asStatus(t.status),
    caseId: t.caseId,
    submitted: day(tl[0]?.at),
    updated: day(tl[tl.length - 1]?.at),
    timeline: tl.map((e, i) => ({
      date: day(e.at),
      text: e.note ?? e.status,
      done: i < tl.length - 1,
    })),
    docs: t.emailDraft?.subject ? [`E-postutkast: ${t.emailDraft.subject}`] : [],
  };
}

/**
 * Ta bort — ärendelista + valfri tavelvy, öppnar ProofDrawer.
 *
 * Läste tidigare MOCK_TICKETS: tre påhittade ärenden (MrKoll GE-4201,
 * Google GE-4190, Ratsit GE-4187) som visades för alla inloggade. Nu hämtas
 * ärendena från /v1/tickets, som bara returnerar kontots egna.
 */
export default function Remove() {
  const [view, setView] = useState<ViewMode>("lista");
  const [proof, setProof] = useState<RemovalTicket | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [tickets, setTickets] = useState<RemovalTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await listTickets();
        if (alive) setTickets((res.data ?? []).map(toCard));
      } catch (e) {
        const msg = e instanceof Error ? e.message : "";
        if (alive) {
          setError(
            msg === "unauthorized"
              ? "Du är utloggad — logga in för att se dina ärenden."
              : "Kunde inte hämta ärenden.",
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const boardCols = useMemo(
    () =>
      BOARD_COLS.map((col) => ({
        ...col,
        items: tickets.filter((t) => col.keys.includes(t.status)),
      })),
    [tickets],
  );

  const seg = (on: boolean) =>
    ({
      padding: "8px 16px",
      border: 0,
      background: on ? ACCENT_100 : "transparent",
      fontSize: 13,
      cursor: "pointer" as const,
      color: on ? ACCENT_900 : "#7d7979",
      fontWeight: on ? 600 : 400,
    }) as const;

  return (
    <div data-page="remove" style={{ fontFamily: '"Lora", Georgia, serif', color: "#201f1d" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <h1
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontWeight: 400,
            fontSize: 34,
            margin: 0,
            color: NAVY,
            flex: 1,
          }}
        >
          Ta bort
        </h1>
        <div
          style={{
            display: "flex",
            border: "1px solid color-mix(in srgb, #201f1d 16%, transparent)",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <button type="button" onClick={() => setView("lista")} style={seg(view === "lista")}>
            Lista
          </button>
          <button type="button" onClick={() => setView("board")} style={seg(view === "board")}>
            Board
          </button>
        </div>
      </div>

      {/* Här stod tidigare "Fullmakt för avindexering: aktiv — signerad med
          BankID 2026-07-01". Det var hårdkodat och sant för ingen. Vi har
          ingen BankID-integration; att påstå det i en dataskyddstjänst är
          precis fel sorts fel. Ersatt med ärendestatus. */}
      {loading && (
        <p style={{ marginTop: 18, color: "#7d7979", fontSize: 14 }}>Hämtar ärenden…</p>
      )}
      {error && (
        <div
          style={{
            marginTop: 18,
            border: `1px solid ${ACCENT_300}`,
            background: ACCENT_100,
            borderRadius: 4,
            padding: "14px 18px",
            fontSize: 13.5,
            color: ACCENT_900,
          }}
        >
          {error}
        </div>
      )}
      {!loading && !error && tickets.length === 0 && (
        <p style={{ marginTop: 18, color: "#7d7979", fontSize: 14 }}>
          Inga ärenden ännu. Godkänn en borttagning under Hitta så skapas ett ärende här.
        </p>
      )}

      {view === "lista" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
          {tickets.map((t) => (
            <TicketCard key={t.id} ticket={t} variant="list" onOpenProof={setProof} />
          ))}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
            marginTop: 20,
            alignItems: "start",
          }}
        >
          {boardCols.map((col) => (
            <div key={col.title}>
              <div
                style={{
                  fontSize: 11.5,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#9b9797",
                  padding: "0 4px 10px",
                }}
              >
                {col.title}{" "}
                <span style={{ fontFeatureSettings: '"tnum" 1' }}>({col.items.length})</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {col.items.map((t) => (
                  <TicketCard
                    key={t.id}
                    ticket={t}
                    variant="board"
                    onOpenProof={setProof}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <ProofDrawer
        ticket={proof}
        open={!!proof}
        onClose={() => setProof(null)}
        onToast={showToast}
      />

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            background: NAVY,
            color: "#fff",
            borderRadius: 4,
            padding: "12px 22px",
            fontSize: 14,
            zIndex: 80,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}
